import { Toaster } from "@/components/ui/toaster"; // app-root
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, keepPreviousData } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Navigate, Routes, Route, useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import ProtectedRoute from "@/components/ProtectedRoute";
import OnboardingGuard from "@/components/OnboardingGuard";
import AgeVerificationGuard from "@/components/AgeVerificationGuard";
import ProtectedVenueRoute from "@/components/ProtectedVenueRoute";
import ProtectedAdminRoute from "@/components/ProtectedAdminRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProfileShell from "@/components/layouts/ProfileShell";
import DiscoverShell from "@/components/layouts/DiscoverShell";
import MatchesShell from "@/components/layouts/MatchesShell";
import VenuesShell from "@/components/layouts/VenuesShell";
import AdminVisualShell from "@/components/layouts/AdminVisualShell";



import DiscoverErrorFallback from "@/components/DiscoverErrorFallback";
import PageTransition from "@/components/PageTransition";
import React, { lazy, Suspense } from "react";
import { BackgroundLocationProvider } from "@/hooks/useBackgroundLocation";
import SessionRoutePersistence from "@/components/SessionRoutePersistence";
import RoutePrefetchTracker from "@/components/RoutePrefetchTracker";
import ScrollProgressivePrefetch from "@/components/ScrollProgressivePrefetch";
import ScrollRestoration from "@/components/ScrollRestoration";
import { RouteTransitionProvider } from "@/components/RouteTransitionContext";
import { traceRouteEvent } from "@/lib/route-debug-trace";
import LoginFlowTelemetry from "@/components/LoginFlowTelemetry";
import MaintenanceGate from "@/components/MaintenanceGate";
import AdminShortcutFAB from "@/components/AdminShortcutFAB";
import PwaUpdateBanner from "@/components/PwaUpdateBanner";
import { FeatureRoute } from "@/components/FeatureGate";
import SplashScreen from "@/components/SplashScreen";
import { LocationPermissionPrimerHost } from "@/components/LocationPermissionPrimer";
import { StillHerePrompt } from "@/components/presence/StillHerePrompt";
import AppLockGate from "@/components/AppLockGate";
import AccountAccess from "@/components/AccountAccess";
import ExclusiveAppAccess from "@/components/ExclusiveAppAccess";
import ActiveAccountEntryGate from "@/components/ActiveAccountEntryGate";
import AccountSessionGate from "@/components/AccountSessionGate";


const AppHooks = () => {
  const { pathname } = useLocation();
  const isBusinessRoute =
    pathname.startsWith("/venue-request") ||
    pathname.startsWith("/venue-management") ||
    pathname.startsWith("/venue-dashboard") ||
    pathname.startsWith("/venue-referral") ||
    pathname.startsWith("/venue-profile") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/auth") ||
    pathname.startsWith("/age-gate") ||
    pathname.startsWith("/adhesion-contract");

  const [mountDeferred, setMountDeferred] = React.useState(false);
  React.useEffect(() => {
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const trigger = () => setMountDeferred(true);
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(trigger, { timeout: 1500 });
    } else {
      const id = window.setTimeout(trigger, 600);
      return () => window.clearTimeout(id);
    }
  }, []);

  if (!mountDeferred) return null;

  return (
    <Suspense fallback={null}>
      <DeferredAppHooks isBusinessRoute={isBusinessRoute} />
    </Suspense>
  );
};

// Fora do primeiro carregamento: a abertura do app é o visual novo (ZappyPage).
const RoleSelect = lazy(() => import("./pages/RoleSelect"));

const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Discover = lazy(() => import("./pages/Discover"));
const NearbyPlaces = lazy(() => import("./pages/NearbyPlaces"));
const Venues = lazy(() => import("./pages/Venues"));
const Matches = lazy(() => import("./pages/Matches"));
const Profile = lazy(() => import("./pages/Profile"));
const NotificationPreferences = lazy(() => import("./pages/NotificationPreferences"));
const ProfilePhoto = lazy(() => import("./pages/ProfilePhoto"));
const AdminAgeVerifications = lazy(() => import("./pages/AdminAgeVerifications"));
const AdminCompanyInfo = lazy(() => import("./pages/AdminCompanyInfo"));
const AdminAppSettings = lazy(() => import("./pages/AdminAppSettings"));
const AdminInstall = lazy(() => import("./pages/AdminInstall"));
const AdminSessionDiagnostics = lazy(() => import("./pages/AdminSessionDiagnostics"));
const AdminSessionExport = lazy(() => import("./pages/AdminSessionExport"));
const AdminFeatureFlags = lazy(() => import("./pages/AdminFeatureFlags"));
const Dpo = lazy(() => import("./pages/Dpo"));
const InternationalTransfers = lazy(() => import("./pages/InternationalTransfers"));
const Appeal = lazy(() => import("./pages/Appeal"));
const Security = lazy(() => import("./pages/Security"));
const AdminIncidentResponse = lazy(() => import("./pages/AdminIncidentResponse"));
const Chat = lazy(() => import("./pages/Chat"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Sobre = lazy(() => import("./pages/Sobre"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VenueManagement = lazy(() => import("./pages/VenueManagement"));
const VenueDashboard = lazy(() => import("./pages/VenueDashboard"));
const VenueMusicNights = lazy(() => import("./pages/VenueMusicNights"));
const VenueHoursEditor = lazy(() => import("./pages/VenueHoursEditor"));
const VenueOwnerManage = lazy(() => import("./pages/VenueOwnerManage"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const VenueReferral = lazy(() => import("./pages/VenueReferral"));
const VenueProfile = lazy(() => import("./pages/VenueProfile"));
const VenueDetail = lazy(() => import("./pages/VenueDetail"));
const VenueQrCheckin = lazy(() => import("./pages/VenueQrCheckin"));
const CheckinEntry = lazy(() => import("./pages/CheckinEntry"));
const PortalVenueAccess = lazy(() => import("./pages/PortalVenueAccess"));
const Analytics = lazy(() => import("./pages/Analytics"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AdminLegalDocuments = lazy(() => import("./pages/AdminLegalDocuments"));
const AdminRlsDenials = lazy(() => import("./pages/AdminRlsDenials"));
const AdminPhotoAccessAudit = lazy(() => import("./pages/AdminPhotoAccessAudit"));
const AdminPushLogs = lazy(() => import("./pages/AdminPushLogs"));
const AdminAccessDenials = lazy(() => import("./pages/AdminAccessDenials"));
const AdminCoinPacks = lazy(() => import("./pages/AdminCoinPacks"));
const AdminPilotCities = lazy(() => import("./pages/AdminPilotCities"));
const AdminCoinRefunds = lazy(() => import("./pages/AdminCoinRefunds"));
const AdminSystemHealth = lazy(() => import("./pages/AdminSystemHealth"));
const AdminObservability = lazy(() => import("./pages/AdminObservability"));
const AdminDemoMode = lazy(() => import("./pages/AdminDemoMode"));
const AdminBarInvite = lazy(() => import("./pages/AdminBarInvite"));
const AdminSubscriptions = lazy(() => import("./pages/AdminSubscriptions"));
const AdminModeration = lazy(() => import("./pages/AdminModeration"));
const AdminUsersMedia = lazy(() => import("./pages/AdminUsersMedia"));
const AdminChats = lazy(() => import("./pages/AdminChats"));
const PublicVenues = lazy(() => import("./pages/PublicVenues"));
const PublicVenuePanel = lazy(() => import("./pages/PublicVenuePanel"));
const PublicVenueRequests = lazy(() => import("./pages/PublicVenueRequests"));
const VenueOwnerHub = lazy(() => import("./pages/VenueOwnerHub"));
const VenueOwnerMobile = lazy(() => import("./pages/VenueOwnerMobile"));
const VenueMusicRequestsReport = lazy(() => import("./pages/VenueMusicRequestsReport"));
const VenueSongRequestDetail = lazy(() => import("./pages/VenueSongRequestDetail"));
const RequesterVenueUpdates = lazy(() => import("./pages/RequesterVenueUpdates"));
const MySongRequests = lazy(() => import("./pages/MySongRequests"));
const VenueRepertoireBrowse = lazy(() => import("./pages/VenueRepertoireBrowse"));
const PushSetup = lazy(() => import("./pages/PushSetup"));
const VenueOwnerCenter = lazy(() => import("./pages/VenueOwnerCenter"));
const PublicShowcase = lazy(() => import("./pages/PublicShowcase"));
const AdminStaff = lazy(() => import("./pages/AdminStaff"));
const AdminAnnouncements = lazy(() => import("./pages/AdminAnnouncements"));
const AdminVenueFavorites = lazy(() => import("./pages/AdminVenueFavorites"));
const AdminNotifications = lazy(() => import("./pages/AdminNotifications"));
const AdminManage = lazy(() => import("./pages/AdminManage"));
const AdminSongRequests = lazy(() => import("./pages/AdminSongRequests"));
const AdminVenueAccounts = lazy(() => import("./pages/AdminVenueAccounts"));
const AdminCreateVenue = lazy(() => import("./pages/AdminCreateVenue"));
const AdminVenueCenter = lazy(() => import("./pages/AdminVenueCenter"));
const AdminVenueHoursNotices = lazy(() => import("./pages/AdminVenueHoursNotices"));
const VenueChat = lazy(() => import("./pages/VenueChat"));
const VenueLogin = lazy(() => import("./pages/VenueLogin"));
const AdminOnboardingFunnel = lazy(() => import("./pages/AdminOnboardingFunnel"));
const AdminFunnelMetrics = lazy(() => import("./pages/AdminFunnelMetrics"));
const AdminEmailSmokeTest = lazy(() => import("./pages/AdminEmailSmokeTest"));
const Plans = lazy(() => import("./pages/Plans"));
const Referral = lazy(() => import("./pages/Referral"));
const VenueRequest = lazy(() => import("./pages/VenueRequest"));
const VerifyVenue = lazy(() => import("./pages/VerifyVenue"));
const WhoLikedYou = lazy(() => import("./pages/WhoLikedYou"));
const DailyPicks = lazy(() => import("./pages/DailyPicks"));
const MyDataRights = lazy(() => import("./pages/MyDataRights"));
const DPIA = lazy(() => import("./pages/DPIA"));
const ComplianceDashboard = lazy(() => import("./pages/ComplianceDashboard"));
const AdminComplianceTracker = lazy(() => import("./pages/AdminComplianceTracker"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Events = lazy(() => import("./pages/Events"));
const AdhesionContract = lazy(() => import("./pages/AdhesionContract"));
const AgeGate = lazy(() => import("./pages/AgeGate"));
const MinorBlocked = lazy(() => import("./pages/MinorBlocked"));
const AgeVerificationRequired = lazy(() => import("./pages/AgeVerificationRequired"));
const VerifyIdentity = lazy(() => import("./pages/VerifyIdentity"));

const ChildSafety = lazy(() => import("./pages/ChildSafety"));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines"));
const ContactPage = lazy(() => import("./pages/Contact"));
const DeleteAccountPublic = lazy(() => import("./pages/DeleteAccountPublic"));
const DeleteAccountFlow = lazy(() => import("./pages/DeleteAccountFlow"));
const SafetyCenter = lazy(() => import("./pages/SafetyCenter"));
const ModerationTransparency = lazy(() => import("./pages/ModerationTransparency"));
const OndeEstamos = lazy(() => import("./pages/OndeEstamos"));
const ParaBares = lazy(() => import("./pages/ParaBares"));
const Parcerias = lazy(() => import("./pages/Parcerias"));
const ParaEstudantes = lazy(() => import("./pages/ParaEstudantes"));
const ParaProfissionais = lazy(() => import("./pages/ParaProfissionais"));
const ParaRecemChegados = lazy(() => import("./pages/ParaRecemChegados"));
const MySubscription = lazy(() => import("./pages/MySubscription"));
const RestorePurchases = lazy(() => import("./pages/RestorePurchases"));
const Paywall = lazy(() => import("./pages/Paywall"));
const AdminReportsSla = lazy(() => import("./pages/AdminReportsSla"));
const MyReceipts = lazy(() => import("./pages/MyReceipts"));
const MyRecordings = lazy(() => import("./pages/MyRecordings"));
const Account = lazy(() => import("./pages/Account"));

const MyCoins = lazy(() => import("./pages/MyCoins"));
const Rewards = lazy(() => import("./pages/Rewards"));

const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const Install = lazy(() => import("./pages/Install"));
const HowToInstall = lazy(() => import("./pages/HowToInstall"));
const ShareGps = lazy(() => import("./pages/ShareGps"));
const MeetupAlert = lazy(() => import("./pages/MeetupAlert"));
const RecoverAccess = lazy(() => import("./pages/RecoverAccess"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const PublicVenuePage = lazy(() => import("./pages/PublicVenuePage"));
const VenueWifiPortal = lazy(() => import("./pages/VenueWifiPortal"));
const VenueInstallPage = lazy(() => import("./pages/VenueInstallPage"));
const DebugLocalizacao = lazy(() => import("./pages/DebugLocalizacao"));
const DiagnosticoVenues = lazy(() => import("./pages/DiagnosticoVenues"));
const VenueAffiliates = lazy(() => import("./pages/VenueAffiliates"));
const Faq = lazy(() => import("./pages/Faq"));
const AdminEditorialVenues = lazy(() => import("./pages/AdminEditorialVenues"));
const AdminGoLive = lazy(() => import("./pages/AdminGoLive"));
const AdminCredits = lazy(() => import("./pages/AdminCredits"));
const AdminLiveMap = lazy(() => import("./pages/AdminLiveMap"));
const AdminThemePreview = lazy(() => import("./pages/AdminThemePreview"));
// Pré-visualização interna do novo visual do Zappy (redesign em andamento, fase 1).
// Fidelidade 1:1 ao protótipo aprovado; dados mock. Remover/promover quando a migração
// tela a tela para dados reais (fases 2-4) estiver concluída.
const ZappyPage = lazy(() => import("./pages/ZappyPage"));
// Montado só depois que o app abre (requestIdleCallback): fica fora do primeiro carregamento.
const DeferredAppHooks = lazy(() => import("@/components/DeferredAppHooks"));
// O aviso de cookies só aparece depois de 1,5 s: carrega à parte para não pesar na abertura.
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const ZappyConsolePage = lazy(() => import("./pages/ZappyConsolePage"));
const ChatComposerPreview = lazy(() => import("./pages/ChatComposerPreview"));
const ChatFeaturesPreview = lazy(() => import("./pages/ChatFeaturesPreview"));
import { areE2EPreviewsEnabled } from "@/lib/e2ePreview";

// Sem loader global: evita "flash" branco entre transições de tela.
// O conteúdo anterior permanece visível até o próximo chunk chegar.
const PageLoader = () => null;

// Padrão stale-while-revalidate global: dado anterior permanece visível
// durante refetches/troca de chave — elimina spinners piscando em todas
// as telas que usam React Query.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      retry: 1,
      placeholderData: keepPreviousData,
    },
  },
});

const P = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children }, ref) => {
    const { pathname } = useLocation();
    const administrative = (pathname.startsWith("/admin") && !pathname.startsWith("/admin/novo-visual") && pathname !== "/admin/theme-preview") || pathname === "/analytics" || pathname === "/compliance";
    return <PageTransition ref={ref}>{administrative ? <AdminVisualShell>{children}</AdminVisualShell> : children}</PageTransition>;
  }
);
P.displayName = "P";

/**
 * Deep link `/match/:matchId` (declarado no AASA e no assetlinks): redireciona
 * para a conversa correspondente, preservando o histórico de navegação.
 */
const MatchDeepLinkRedirect = () => {
  const { matchId } = useParams();
  return <Navigate to={matchId ? `/chat/${matchId}` : "/matches"} replace />;
};

/** Mantém atalhos e notificações antigas apontando para o painel unificado. */
const LegacyVenuePanelRedirect = () => {
  const { venueId } = useParams();
  return <Navigate to={venueId ? `/venue-dashboard/${venueId}/gerenciar?aba=painel` : "/meu-bar"} replace />;
};



const AnimatedRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [displayLocation, setDisplayLocation] = React.useState(location);
  const [, startTransition] = React.useTransition();
  const displayRoute = `${displayLocation.pathname}${displayLocation.search}${displayLocation.hash}`;
  const nextRoute = `${location.pathname}${location.search}${location.hash}`;

  // Visual moderno em 100% do app: todas as telas herdam os tokens claros do shell Zappy.
  React.useEffect(() => {
    document.documentElement.classList.add("app-visual-active");
  }, []);

  // QR do bar lido pela câmera do celular abre direto a tela do app nativo.
  const navigateRef = React.useRef(navigate);
  navigateRef.current = navigate;
  React.useEffect(() => {
    let cleanup: () => void = () => {};
    let cancelled = false;
    void import("@/lib/native/deepLinks")
      .then(({ registerNativeDeepLinks }) => registerNativeDeepLinks((route) => navigateRef.current(route)))
      .then((remove) => {
        if (cancelled) remove();
        else cleanup = remove;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      cleanup();
    };
  }, []);

  React.useEffect(() => {
    if (displayRoute === nextRoute) return;
    // startTransition + Suspense: a tela atual permanece visível
    // enquanto o chunk da próxima rota carrega. Sem flash, sem loader.
    traceRouteEvent("AnimatedRoutes:location", nextRoute, {
      displayRoute,
      nextRoute,
    });
    startTransition(() => setDisplayLocation(location));
  }, [displayRoute, location, nextRoute]);

  return (
  <Routes location={displayLocation}>
    <Route path="/" element={<ZappyPage />} />
    {/* Abas principais antigas → visual novo já na aba certa (ver userEntryFromPath). */}
    <Route path="/discover" element={<ZappyPage />} />
    <Route path="/nearby" element={<ZappyPage />} />
    <Route path="/matches" element={<ZappyPage />} />
    <Route path="/venues" element={<ZappyPage />} />
    <Route path="/events" element={<ZappyPage />} />
    <Route path="/index" element={<Navigate to="/" replace />} />
    {/* Telas estáticas de apoio aos testes de interface (sem dados reais).
        Ficam fora do app publicado. */}
    {areE2EPreviewsEnabled() && (
      <Route path="/__e2e/chat-composer" element={<P><ChatComposerPreview /></P>} />
    )}
    {areE2EPreviewsEnabled() && (
      <Route path="/__e2e/chat-features" element={<P><ChatFeaturesPreview /></P>} />
    )}
    <Route path="/role-select" element={<P><RoleSelect /></P>} />
    <Route path="/auth" element={<P><Auth /></P>} />
    <Route path="/entrar-bar" element={<P><VenueLogin /></P>} />
    <Route path="/reset-password" element={<P><ResetPassword /></P>} />
    <Route path="/terms" element={<P><Terms /></P>} />
    <Route path="/bares" element={<P><PublicVenues /></P>} />
    <Route path="/bar/:id" element={<P><PublicVenuePanel /></P>} />
    <Route path="/bar/:id/pedidos" element={<P><PublicVenueRequests /></P>} />
    <Route path="/publico" element={<P><PublicShowcase /></P>} />
    <Route path="/termos" element={<P><Terms /></P>} />
    <Route path="/privacy" element={<P><Privacy /></P>} />
    <Route path="/privacidade" element={<P><Privacy /></P>} />
    <Route path="/install" element={<P><Install /></P>} />
    <Route path="/como-instalar" element={<P><HowToInstall /></P>} />
    <Route path="/sobre" element={<P><Sobre /></P>} />
    <Route path="/faq" element={<P><Faq /></P>} />
    <Route path="/venue-request" element={<P><VenueRequest /></P>} />
    <Route path="/account" element={<AccountAccess><P><Account /></P></AccountAccess>} />
    <Route path="/verify-venue" element={<P><VerifyVenue /></P>} />
    <Route path="/cookies" element={<P><Cookies /></P>} />
    <Route path="/age-gate" element={<P><AgeGate /></P>} />
    <Route path="/minor-blocked" element={<P><MinorBlocked /></P>} />
    <Route path="/verify-age" element={<ProtectedRoute><P><AgeVerificationRequired /></P></ProtectedRoute>} />
    <Route path="/verificar-identidade" element={<ProtectedRoute><P><VerifyIdentity /></P></ProtectedRoute>} />

    <Route path="/child-safety" element={<P><ChildSafety /></P>} />
    <Route path="/community-guidelines" element={<P><CommunityGuidelines /></P>} />
    <Route path="/diretrizes" element={<P><CommunityGuidelines /></P>} />
    <Route path="/contact" element={<P><ContactPage /></P>} />
    <Route path="/support" element={<P><ContactPage /></P>} />
    <Route path="/suporte" element={<P><ContactPage /></P>} />
    <Route path="/delete-account" element={<P><DeleteAccountPublic /></P>} />
    <Route path="/excluir-conta" element={<P><DeleteAccountPublic /></P>} />
    <Route path="/settings/excluir-conta" element={<AccountAccess><P><DeleteAccountFlow /></P></AccountAccess>} />
    <Route path="/meu-bar/excluir-conta" element={<AccountAccess><P><DeleteAccountFlow /></P></AccountAccess>} />
    <Route path="/safety-center" element={<P><SafetyCenter /></P>} />
    <Route path="/moderation-transparency" element={<P><ModerationTransparency /></P>} />
    <Route path="/onde-estamos" element={<P><OndeEstamos /></P>} />
    <Route path="/para-bares" element={<P><ParaBares /></P>} />
    <Route path="/parcerias" element={<P><Parcerias /></P>} />
    <Route path="/para-estudantes" element={<P><ParaEstudantes /></P>} />
    <Route path="/para-profissionais" element={<P><ParaProfissionais /></P>} />
    <Route path="/para-recem-chegados" element={<P><ParaRecemChegados /></P>} />
    <Route path="/share/gps" element={<P><ShareGps /></P>} />
    <Route path="/aviso-seguranca" element={<P><MeetupAlert /></P>} />
    <Route path="/aviso-encontro" element={<P><MeetupAlert /></P>} />
    <Route path="/recover-access" element={<P><RecoverAccess /></P>} />
    {/* Deep link público de perfil: zappy://u/apelido e https://zappyconecta.com/u/apelido */}
    <Route path="/u/:username" element={<P><PublicProfile /></P>} />
    <Route path="/aqui/:slug" element={<P><PublicVenuePage /></P>} />
    <Route path="/wifi/:slug" element={<P><VenueWifiPortal /></P>} />
    <Route path="/acesso-local" element={<P><PortalVenueAccess /></P>} />
    <Route path="/instalar/:slug" element={<P><VenueInstallPage /></P>} />

    <Route path="/plans" element={<P><Plans /></P>} />
    <Route path="/checkout/return" element={<P><CheckoutReturn /></P>} />
    <Route path="/contract" element={<ProtectedRoute><P><AdhesionContract /></P></ProtectedRoute>} />
    {/* Módulo "Descobrir" com layout persistente: a BottomNav é montada uma
        única vez pelo DiscoverShell e nunca é recriada entre as telas. */}
    <Route element={<DiscoverShell />}>
      {/* /nearby é um atalho: abre o /discover já no modo "Perto de mim" (pessoas próximas). */}
      {/* /nearby-places continua disponível para quem quer só locais/eventos da região, sem pessoas. */}
      <Route path="/nearby-places" element={<ProtectedRoute><P><NearbyPlaces /></P></ProtectedRoute>} />


    </Route>

    {/* Módulo "Locais" com layout persistente: lista, mapa, favoritos,
        detalhes do local, eventos, ranking e check-in compartilham a mesma
        BottomNav fixa, montada uma única vez. */}
    <Route element={<VenuesShell />}>
      <Route path="/checkin" element={<ProtectedRoute><AgeVerificationGuard><P><CheckinEntry /></P></AgeVerificationGuard></ProtectedRoute>} />
      <Route path="/venue/:id" element={<ProtectedRoute><P><VenueDetail /></P></ProtectedRoute>} />
      <Route path="/venue/:id/checkin" element={<ProtectedRoute><AgeVerificationGuard><P><VenueQrCheckin /></P></AgeVerificationGuard></ProtectedRoute>} />
      <Route path="/venue/:id/chat" element={<ProtectedRoute><AgeVerificationGuard><P><VenueChat /></P></AgeVerificationGuard></ProtectedRoute>} />
      <Route path="/venue/:id/repertorio" element={<ProtectedRoute><AgeVerificationGuard><P><VenueRepertoireBrowse /></P></AgeVerificationGuard></ProtectedRoute>} />
      <Route path="/catalogo-musical" element={<Navigate to="/venues" replace />} />
    <Route path="/venue/:id/pedidos" element={<ProtectedRoute><P><PublicVenueRequests /></P></ProtectedRoute>} />
      <Route path="/meus-pedidos-musica" element={<ProtectedRoute><P><MySongRequests /></P></ProtectedRoute>} />
      <Route path="/meus-avisos-bares" element={<ProtectedRoute><P><RequesterVenueUpdates /></P></ProtectedRoute>} />
      <Route path="/configurar-avisos" element={<ProtectedRoute><P><PushSetup /></P></ProtectedRoute>} />
    </Route>


    {/* Área de conexões com navegação persistente. */}
    <Route element={<MatchesShell />}>
      <Route path="/who-liked-you" element={<ProtectedRoute><AgeVerificationGuard><P><WhoLikedYou /></P></AgeVerificationGuard></ProtectedRoute>} />
      <Route path="/destaques" element={<ProtectedRoute><AgeVerificationGuard><OnboardingGuard><P><DailyPicks /></P></OnboardingGuard></AgeVerificationGuard></ProtectedRoute>} />
    </Route>


    <Route path="/chat" element={<Navigate to="/matches" replace />} />
    <Route path="/chat/:matchId" element={<ProtectedRoute><AgeVerificationGuard><FeatureRoute flag="chat_enabled" title="Chat indisponível"><P><Chat /></P></FeatureRoute></AgeVerificationGuard></ProtectedRoute>} />
    {/* Deep link declarado no AASA/assetlinks: /match/:id abre a conversa do match. */}
    <Route path="/match" element={<Navigate to="/matches" replace />} />
    <Route path="/match/:matchId" element={<ProtectedRoute><AgeVerificationGuard><MatchDeepLinkRedirect /></AgeVerificationGuard></ProtectedRoute>} />

    {/* Área "Perfil" com layout persistente: a BottomNav é montada uma única
        vez pelo ProfileShell e não é recriada entre as telas. */}
    <Route element={<ProfileShell />}>
      <Route path="/profile" element={<ProtectedRoute><P><Profile /></P></ProtectedRoute>} />
      <Route path="/profile/photo" element={<ProtectedRoute><P><ProfilePhoto /></P></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><P><NotificationPreferences /></P></ProtectedRoute>} />
      <Route path="/my-subscription" element={<ProtectedRoute><P><MySubscription /></P></ProtectedRoute>} />
      <Route path="/my-subscription/receipts" element={<ProtectedRoute><P><MyReceipts /></P></ProtectedRoute>} />
      <Route path="/minhas-gravacoes" element={<ProtectedRoute><P><MyRecordings /></P></ProtectedRoute>} />

      <Route path="/my-coins" element={<ProtectedRoute><FeatureRoute flag="coins_enabled" title="Moedas Zappy indisponíveis"><P><MyCoins /></P></FeatureRoute></ProtectedRoute>} />
      <Route path="/rewards" element={<ProtectedRoute><FeatureRoute flag="coins_enabled" title="Moedas Zappy indisponíveis"><P><Rewards /></P></FeatureRoute></ProtectedRoute>} />
      <Route path="/meus-dados" element={<ProtectedRoute><P><MyDataRights /></P></ProtectedRoute>} />
      <Route path="/referral" element={<ProtectedRoute><P><Referral /></P></ProtectedRoute>} />
    </Route>
    <Route path="/paywall" element={<ProtectedRoute><P><Paywall /></P></ProtectedRoute>} />
    <Route path="/restore-purchases" element={<ProtectedRoute><P><RestorePurchases /></P></ProtectedRoute>} />
    <Route path="/admin/reports-sla" element={<ProtectedAdminRoute><P><AdminReportsSla /></P></ProtectedAdminRoute>} />


    <Route path="/settings/debug-localizacao" element={<ProtectedRoute><P><DebugLocalizacao /></P></ProtectedRoute>} />
    <Route path="/settings/diagnostico-venues" element={<ProtectedRoute><P><DiagnosticoVenues /></P></ProtectedRoute>} />
    <Route path="/dpia" element={<P><DPIA /></P>} />
    <Route path="/dpo" element={<P><Dpo /></P>} />
    <Route path="/transferencias-internacionais" element={<P><InternationalTransfers /></P>} />
    <Route path="/security" element={<P><Security /></P>} />
    <Route path="/.well-known/security.txt" element={<P><Security /></P>} />
    <Route path="/appeal/:strikeId" element={<ProtectedRoute><P><Appeal /></P></ProtectedRoute>} />
    <Route path="/admin/incidents" element={<ProtectedAdminRoute><P><AdminIncidentResponse /></P></ProtectedAdminRoute>} />

    <Route path="/parceiro" element={<ProtectedVenueRoute><P><PartnerDashboard /></P></ProtectedVenueRoute>} />
    <Route path="/venue-management" element={<ProtectedVenueRoute><P><VenueManagement /></P></ProtectedVenueRoute>} />
    <Route path="/meu-bar" element={<ProtectedVenueRoute><P><VenueOwnerHub /></P></ProtectedVenueRoute>} />
    <Route path="/meu-bar/previa-categoria" element={<Navigate to="/meu-bar" replace />} />
    <Route path="/meu-bar/pedidos" element={<ProtectedVenueRoute><P><VenueOwnerMobile /></P></ProtectedVenueRoute>} />
    <Route path="/meu-bar/pedidos/:requestId" element={<ProtectedVenueRoute><P><VenueSongRequestDetail /></P></ProtectedVenueRoute>} />
    <Route path="/meu-bar/som" element={<ProtectedVenueRoute><Navigate to="/meu-bar/pedidos" replace /></ProtectedVenueRoute>} />
    <Route path="/meu-bar/tocar-manual" element={<ProtectedVenueRoute><Navigate to="/meu-bar/pedidos" replace /></ProtectedVenueRoute>} />
    <Route path="/meu-bar/historico-execucao" element={<ProtectedVenueRoute><Navigate to="/meu-bar/pedidos" replace /></ProtectedVenueRoute>} />
    <Route path="/meu-bar/mais-pedidas" element={<ProtectedVenueRoute><P><VenueMusicRequestsReport /></P></ProtectedVenueRoute>} />
    <Route path="/meu-bar/status-musicas" element={<ProtectedVenueRoute><Navigate to="/meu-bar/pedidos" replace /></ProtectedVenueRoute>} />
    <Route path="/meu-bar/central" element={<ProtectedVenueRoute><P><VenueOwnerCenter /></P></ProtectedVenueRoute>} />
    <Route path="/venue-dashboard/:venueId" element={<ProtectedVenueRoute><P><VenueDashboard /></P></ProtectedVenueRoute>} />
    <Route path="/venue-dashboard/:venueId/musica" element={<ProtectedVenueRoute><P><VenueMusicNights /></P></ProtectedVenueRoute>} />
    <Route path="/venue-dashboard/:venueId/pedidos" element={<ProtectedVenueRoute><LegacyVenuePanelRedirect /></ProtectedVenueRoute>} />
    <Route path="/venue-dashboard/:venueId/repertorio" element={<ProtectedVenueRoute><LegacyVenuePanelRedirect /></ProtectedVenueRoute>} />
    <Route path="/venue-dashboard/:venueId/chat" element={<ProtectedVenueRoute><LegacyVenuePanelRedirect /></ProtectedVenueRoute>} />
    <Route path="/venue-dashboard/:venueId/horarios" element={<ProtectedVenueRoute><P><VenueHoursEditor /></P></ProtectedVenueRoute>} />
    <Route path="/venue-dashboard/:venueId/gerenciar" element={<ProtectedVenueRoute><P><VenueOwnerManage /></P></ProtectedVenueRoute>} />
    <Route path="/venue-referral/:venueId" element={<ProtectedVenueRoute><P><VenueReferral /></P></ProtectedVenueRoute>} />
    <Route path="/venue-affiliates" element={<ProtectedVenueRoute><P><VenueAffiliates /></P></ProtectedVenueRoute>} />
    <Route path="/venue-profile" element={<ProtectedVenueRoute><P><VenueProfile /></P></ProtectedVenueRoute>} />
    <Route path="/admin/login" element={<ZappyPage />} />
    {/* Desde 01/11 /admin abre a Administração do visual novo; o painel antigo fica em /admin/painel-completo. */}
    <Route path="/admin" element={<ProtectedAdminRoute><P><ZappyPage /></P></ProtectedAdminRoute>} />
    <Route path="/admin/painel-completo" element={<ProtectedAdminRoute><P><AdminPanel /></P></ProtectedAdminRoute>} />
    <Route path="/analytics" element={<ProtectedAdminRoute><P><Analytics /></P></ProtectedAdminRoute>} />
    <Route path="/compliance" element={<ProtectedAdminRoute><P><ComplianceDashboard /></P></ProtectedAdminRoute>} />
    <Route path="/admin/compliance-tracker" element={<ProtectedAdminRoute><P><AdminComplianceTracker /></P></ProtectedAdminRoute>} />
    <Route path="/admin/age-verifications" element={<ProtectedAdminRoute><P><AdminAgeVerifications /></P></ProtectedAdminRoute>} />
    <Route path="/admin/legal" element={<ProtectedAdminRoute><P><AdminLegalDocuments /></P></ProtectedAdminRoute>} />
    <Route path="/admin/rls-denials" element={<ProtectedAdminRoute><P><AdminRlsDenials /></P></ProtectedAdminRoute>} />
    <Route path="/admin/access-denials" element={<ProtectedAdminRoute><P><AdminAccessDenials /></P></ProtectedAdminRoute>} />
    <Route path="/admin/photo-access-audit" element={<ProtectedAdminRoute><P><AdminPhotoAccessAudit /></P></ProtectedAdminRoute>} />
    <Route path="/admin/push-logs" element={<ProtectedAdminRoute><P><AdminPushLogs /></P></ProtectedAdminRoute>} />
    <Route path="/admin/coin-packs" element={<ProtectedAdminRoute><P><AdminCoinPacks /></P></ProtectedAdminRoute>} />
    <Route path="/admin/cidades" element={<ProtectedAdminRoute><P><AdminPilotCities /></P></ProtectedAdminRoute>} />
    <Route path="/admin/coin-refunds" element={<ProtectedAdminRoute><P><AdminCoinRefunds /></P></ProtectedAdminRoute>} />
    <Route path="/admin/system-health" element={<ProtectedAdminRoute><P><AdminSystemHealth /></P></ProtectedAdminRoute>} />
    <Route path="/admin/moderacao" element={<ProtectedAdminRoute><P><AdminModeration /></P></ProtectedAdminRoute>} />
    <Route path="/admin/usuarios" element={<ProtectedAdminRoute><P><AdminUsersMedia /></P></ProtectedAdminRoute>} />
    <Route path="/admin/licencas-musicais" element={<ProtectedAdminRoute><Navigate to="/admin" replace /></ProtectedAdminRoute>} />
    <Route path="/admin/pedidos-musica" element={<ProtectedAdminRoute><P><AdminSongRequests /></P></ProtectedAdminRoute>} />
    <Route path="/admin/bares" element={<ProtectedAdminRoute><P><AdminVenueAccounts /></P></ProtectedAdminRoute>} />
    <Route path="/admin/bares/novo" element={<ProtectedAdminRoute><P><AdminCreateVenue /></P></ProtectedAdminRoute>} />
    <Route path="/admin/central-bares" element={<ProtectedAdminRoute><P><AdminVenueCenter /></P></ProtectedAdminRoute>} />
    <Route path="/admin/gestao" element={<ProtectedAdminRoute><P><AdminManage /></P></ProtectedAdminRoute>} />
    <Route path="/admin/conversas" element={<ProtectedAdminRoute><P><AdminChats /></P></ProtectedAdminRoute>} />
    <Route path="/admin/equipe" element={<ProtectedAdminRoute><P><AdminStaff /></P></ProtectedAdminRoute>} />
    <Route path="/admin/anuncios" element={<ProtectedAdminRoute><P><AdminAnnouncements /></P></ProtectedAdminRoute>} />
    <Route path="/admin/favoritos" element={<ProtectedAdminRoute><P><AdminVenueFavorites /></P></ProtectedAdminRoute>} />
    <Route path="/admin/notificacoes" element={<ProtectedAdminRoute><P><AdminNotifications /></P></ProtectedAdminRoute>} />
    <Route path="/admin/avisos-horarios" element={<ProtectedAdminRoute><P><AdminVenueHoursNotices /></P></ProtectedAdminRoute>} />
    <Route path="/admin/demo" element={<ProtectedAdminRoute><P><AdminDemoMode /></P></ProtectedAdminRoute>} />
    <Route path="/admin/convite-bar" element={<ProtectedAdminRoute><P><AdminBarInvite /></P></ProtectedAdminRoute>} />
    <Route path="/admin/assinaturas" element={<ProtectedAdminRoute><P><AdminSubscriptions /></P></ProtectedAdminRoute>} />
    <Route path="/admin/observability" element={<ProtectedAdminRoute><P><AdminObservability /></P></ProtectedAdminRoute>} />
    <Route path="/admin/onboarding-funnel" element={<ProtectedAdminRoute><P><AdminOnboardingFunnel /></P></ProtectedAdminRoute>} />
    <Route path="/admin/funnel-metrics" element={<ProtectedAdminRoute><P><AdminFunnelMetrics /></P></ProtectedAdminRoute>} />
    <Route path="/admin/company-info" element={<ProtectedAdminRoute><P><AdminCompanyInfo /></P></ProtectedAdminRoute>} />
    <Route path="/admin/configuracoes" element={<ProtectedAdminRoute><P><AdminAppSettings /></P></ProtectedAdminRoute>} />
    <Route path="/admin/feature-flags" element={<ProtectedAdminRoute><P><AdminFeatureFlags /></P></ProtectedAdminRoute>} />
    <Route path="/admin/email-smoke-test" element={<ProtectedAdminRoute><P><AdminEmailSmokeTest /></P></ProtectedAdminRoute>} />
    <Route path="/admin/editorial-venues" element={<ProtectedAdminRoute><P><AdminEditorialVenues /></P></ProtectedAdminRoute>} />
    <Route path="/admin/install" element={<ProtectedAdminRoute><P><AdminInstall /></P></ProtectedAdminRoute>} />
    <Route path="/admin/go-live" element={<ProtectedAdminRoute><P><AdminGoLive /></P></ProtectedAdminRoute>} />
    <Route path="/admin/credits" element={<ProtectedAdminRoute><P><AdminCredits /></P></ProtectedAdminRoute>} />
    <Route path="/admin/theme-preview" element={<ProtectedAdminRoute><P><AdminThemePreview /></P></ProtectedAdminRoute>} />
    {/* Pré-visualização interna do redesign do Zappy — fase 1 (ver .lovable/plan/). Só para admin. */}
    <Route path="/admin/novo-visual" element={<ProtectedAdminRoute><P><ZappyPage /></P></ProtectedAdminRoute>} />
    <Route path="/admin/novo-visual/console" element={<ProtectedAdminRoute><P><ZappyConsolePage /></P></ProtectedAdminRoute>} />
    <Route path="/admin/live-map" element={<ProtectedAdminRoute><P><AdminLiveMap /></P></ProtectedAdminRoute>} />
    <Route path="/admin/session-diagnostics" element={<ProtectedAdminRoute><P><AdminSessionDiagnostics /></P></ProtectedAdminRoute>} />
    <Route path="/admin/session-export" element={<ProtectedAdminRoute><P><AdminSessionExport /></P></ProtectedAdminRoute>} />



    <Route path="*" element={<P><NotFound /></P>} />
  </Routes>
  );
};

const App = () => (
  <ErrorBoundary>
    
    <HelmetProvider>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ExclusiveAppAccess>
            <AuthProvider>
              <AccountSessionGate>
              <BackgroundLocationProvider>
                {/* App shell: garante background escuro em qualquer estado
                    (rota carregando, Suspense vazio, erro inicial) — sem
                    flash branco entre transições. */}
                {/* App stage: fundo escuro fora do frame em telas largas (padrão Tinder web). */}
                <div className="min-h-dvh bg-background text-foreground flex justify-center">
                  {/* App frame: 100% em mobile; em tablet/desktop centraliza num "telefone"
                      com largura máxima e altura dinâmica, evitando layout esticado.
                      Usamos <main> aqui (landmark único da página) p/ atender a regra
                      a11y "landmark-one-main" do Lighthouse — páginas internas que
                      precisem de estrutura adicional usam <section>. */}
                  <main
                    id="main-content"
                    className="app-frame w-full min-h-dvh bg-background text-foreground safe-bottom relative"
                  >
                    <RouteTransitionProvider>
                      <AppHooks />
                      <LoginFlowTelemetry />
                      <SessionRoutePersistence />
                      <RoutePrefetchTracker />
                      <ScrollProgressivePrefetch />
                      <ScrollRestoration />
                      <Suspense fallback={null}><CookieConsent /></Suspense>
                      {/* Sem fallback visível: a tela anterior permanece até o próximo chunk chegar,
                          eliminando qualquer loading/skeleton na transição. */}
                      <Suspense fallback={null}>
                        <MaintenanceGate>
                          <ActiveAccountEntryGate><AnimatedRoutes /></ActiveAccountEntryGate>
                        </MaintenanceGate>
                      </Suspense>
                      <AdminShortcutFAB />
                      <PwaUpdateBanner />
                      <SplashScreen />
                      <LocationPermissionPrimerHost />
                      <StillHerePrompt />
                      <AppLockGate />
                    </RouteTransitionProvider>
                  </main>
                </div>
              </BackgroundLocationProvider>
            </AccountSessionGate>
            </AuthProvider>
            </ExclusiveAppAccess>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
    </HelmetProvider>
    
  </ErrorBoundary>
);

export default App;
